import imageCompression from 'browser-image-compression';

export interface CompressionOptions {
  maxWidthOrHeight?: number;
  maxSizeMB?: number;
  fileType?: string;
}

export interface ThumbnailSize {
  width: number;
  height: number;
}

/**
 * Compress an image file to WebP format
 * @param file - Image file to compress
 * @param options - Compression options
 * @returns Compressed image as Blob
 */
export async function compressImage(
  file: File | Blob,
  options: CompressionOptions = {},
): Promise<Blob> {
  const defaultOptions = {
    maxWidthOrHeight: 1920,
    maxSizeMB: 2,
    fileType: 'image/webp',
    useWebWorker: true,
    initialQuality: 0.9,
  };

  const compressionOptions = { ...defaultOptions, ...options };

  try {
    const compressedFile = await imageCompression(file as File, compressionOptions);
    return compressedFile;
  } catch (error) {
    throw new Error(
      `Failed to compress image: ${error instanceof Error ? error.message : String(error)}`,
    );
  }
}

/**
 * Generate a thumbnail from an image
 * @param file - Image file to generate thumbnail from
 * @param size - Thumbnail size (128x128 for avatars, 256x256 for galleries)
 * @returns Thumbnail as Blob
 */
export async function generateThumbnail(
  file: File | Blob,
  size: ThumbnailSize | 'avatar' | 'gallery',
): Promise<Blob> {
  const sizeMap: Record<string, ThumbnailSize> = {
    avatar: { width: 128, height: 128 },
    gallery: { width: 256, height: 256 },
  };

  const thumbnailSize = typeof size === 'string' ? sizeMap[size] : size;

  const options = {
    maxWidthOrHeight: Math.max(thumbnailSize.width, thumbnailSize.height),
    maxSizeMB: 0.5, // Thumbnails should be small
    fileType: 'image/webp',
    useWebWorker: true,
    initialQuality: 0.85,
  };

  try {
    const thumbnail = await imageCompression(file as File, options);
    return thumbnail;
  } catch (error) {
    throw new Error(
      `Failed to generate thumbnail: ${error instanceof Error ? error.message : String(error)}`,
    );
  }
}

/**
 * Load an image from a file or Blob and return as Image element
 * @param input - File or Blob to load
 * @returns Promise resolving to HTMLImageElement
 */
export function loadImageFromBlob(input: File | Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(input);

    img.addEventListener('load', () => {
      URL.revokeObjectURL(url);
      resolve(img);
    });

    img.addEventListener('error', () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image'));
    });

    img.src = url;
  });
}

/**
 * Validate that a file is a supported image type
 * @param file - File to validate
 * @returns true if file is a supported image type
 */
export function isValidImageFile(file: File): boolean {
  const supportedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];
  return supportedTypes.includes(file.type);
}

/**
 * Get image dimensions from a file or Blob
 * @param input - File or Blob to get dimensions from
 * @returns Promise resolving to { width, height }
 */
export async function getImageDimensions(
  input: File | Blob,
): Promise<{ width: number; height: number }> {
  const img = await loadImageFromBlob(input);
  return {
    width: img.naturalWidth,
    height: img.naturalHeight,
  };
}
